import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Bold, Italic, Underline, List, ListOrdered, Link as LinkIcon } from 'lucide-react';
import { normalizeRichText, sanitizeRichText } from '../utils/richText';

const toolbarActions = [
  { command: 'bold', icon: Bold, label: 'Bold' },
  { command: 'italic', icon: Italic, label: 'Italic' },
  { command: 'underline', icon: Underline, label: 'Underline' },
  { command: 'insertUnorderedList', icon: List, label: 'Bullet list' },
  { command: 'insertOrderedList', icon: ListOrdered, label: 'Numbered list' }
];

function RichTextInput({ value, onChange, placeholder = 'Write something...', className = '', ...rest }) {
  const editorRef = useRef(null);
  const [focused, setFocused] = useState(false);
  const [activeFormats, setActiveFormats] = useState({
    bold: false,
    italic: false,
    underline: false,
    link: false
  });
  const lastValueRef = useRef(value || '');
  const selectionRef = useRef(null);

  useEffect(() => {
    const editor = editorRef.current;
    if (!editor) return;
    if (focused) return;
    if (lastValueRef.current === value) return;
    const normalized = normalizeRichText(value || '');
    editor.innerHTML = normalized;
    lastValueRef.current = value || '';
  }, [value, focused]);

  const findClosestAnchor = (node, editor) => {
    let current = node;
    while (current && current !== editor) {
      if (current.nodeType === Node.ELEMENT_NODE && current.tagName.toLowerCase() === 'a') {
        return current;
      }
      current = current.parentNode;
    }
    return null;
  };

  const updateActiveState = useCallback(() => {
    const editor = editorRef.current;
    if (!editor || !focused) return;
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;
    const range = selection.getRangeAt(0);
    if (!editor.contains(range.commonAncestorContainer)) return;
    const bold = document.queryCommandState('bold');
    const italic = document.queryCommandState('italic');
    const underline = document.queryCommandState('underline');
    const anchor = findClosestAnchor(selection.focusNode, editor);
    setActiveFormats({
      bold,
      italic,
      underline,
      link: Boolean(anchor)
    });
  }, [focused]);

  useEffect(() => {
    const handleSelectionChange = () => {
      updateActiveState();
    };

    document.addEventListener('selectionchange', handleSelectionChange);
    return () => document.removeEventListener('selectionchange', handleSelectionChange);
  }, [updateActiveState]);

  const saveSelection = () => {
    const editor = editorRef.current;
    if (!editor) return;
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;
    const range = selection.getRangeAt(0);
    if (!editor.contains(range.commonAncestorContainer)) return;
    selectionRef.current = range;
  };

  const restoreSelection = () => {
    const editor = editorRef.current;
    if (!editor) return;
    const selection = window.getSelection();
    if (!selection || !selectionRef.current) return;
    selection.removeAllRanges();
    selection.addRange(selectionRef.current);
  };

  const getSelectionRange = () => {
    const editor = editorRef.current;
    if (!editor) return null;
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      if (editor.contains(range.commonAncestorContainer)) {
        return range;
      }
    }
    return selectionRef.current || null;
  };

  const unwrapAnchor = (anchor) => {
    const parent = anchor.parentNode;
    if (!parent) return;
    while (anchor.firstChild) {
      parent.insertBefore(anchor.firstChild, anchor);
    }
    parent.removeChild(anchor);
  };

  const emitChange = () => {
    const editor = editorRef.current;
    if (!editor) return;
    const text = editor.textContent || '';
    const html = editor.innerHTML;
    const hasList = /<(ul|ol|li)\b/i.test(html);
    if (!text.trim() && !hasList) {
      editor.innerHTML = '';
      lastValueRef.current = '';
      onChange('');
      return;
    }
    lastValueRef.current = html;
    onChange(html);
    updateActiveState();
  };

  const applyCommand = (command, valueArg = null) => {
    const editor = editorRef.current;
    if (!editor) return;
    saveSelection();
    editor.focus({ preventScroll: true });
    restoreSelection();
    document.execCommand(command, false, valueArg);
    saveSelection();
    emitChange();
    updateActiveState();
  };

  const handleLink = () => {
    const editor = editorRef.current;
    if (!editor) return;
    const selection = window.getSelection();
    const range = getSelectionRange();
    if (!range || range.collapsed) {
      window.alert('Highlight the text you want to link first.');
      return;
    }
    if (selection) {
      selection.removeAllRanges();
      selection.addRange(range);
    }
    const activeAnchor = findClosestAnchor(range.commonAncestorContainer, editor);

    if (activeAnchor) {
      unwrapAnchor(activeAnchor);
      saveSelection();
      emitChange();
      updateActiveState();
      return;
    }

    const url = window.prompt('Enter a URL (https://...)');
    if (!url) return;

    const anchor = document.createElement('a');
    anchor.setAttribute('href', url);
    anchor.setAttribute('target', '_blank');
    anchor.setAttribute('rel', 'noopener noreferrer');
    const contents = range.extractContents();
    anchor.appendChild(contents);
    range.insertNode(anchor);
    if (selection) {
      const newRange = document.createRange();
      newRange.selectNodeContents(anchor);
      selection.removeAllRanges();
      selection.addRange(newRange);
    }
    saveSelection();
    emitChange();
    updateActiveState();
  };

  const handleToolbarAction = (action, event) => {
    event.preventDefault();
    const editor = editorRef.current;
    if (!editor) return;
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      if (editor.contains(range.commonAncestorContainer)) {
        selectionRef.current = range;
      }
    }

    if (action === 'insertUnorderedList') {
      applyListCommand('insertUnorderedList', 'ul');
      return;
    }

    if (action === 'insertOrderedList') {
      applyListCommand('insertOrderedList', 'ol');
      return;
    }

    applyCommand(action);
  };

  const applyList = (listTag) => {
    const editor = editorRef.current;
    if (!editor) return;
    const selection = window.getSelection();
    let range = getSelectionRange();

    if (!range) {
      range = document.createRange();
      range.selectNodeContents(editor);
      range.collapse(false);
    }

    editor.focus({ preventScroll: true });
    if (selection) {
      selection.removeAllRanges();
      selection.addRange(range);
    }

    const listEl = document.createElement(listTag);
    listEl.style.paddingLeft = '20px';

    if (range.collapsed) {
      const li = document.createElement('li');
      const placeholder = document.createTextNode('\u200B');
      li.appendChild(placeholder);
      listEl.appendChild(li);
      range.insertNode(listEl);
      const newRange = document.createRange();
      newRange.setStart(placeholder, 1);
      newRange.collapse(true);
      if (selection) {
        selection.removeAllRanges();
        selection.addRange(newRange);
      }
      selectionRef.current = newRange;
    } else {
      const selectedText = range.toString();
      range.deleteContents();
      const lines = selectedText.split(/\n+/).map((line) => line.trim()).filter(Boolean);
      if (lines.length === 0) {
        const li = document.createElement('li');
        li.appendChild(document.createTextNode(selectedText));
        listEl.appendChild(li);
      } else {
        lines.forEach((line) => {
          const li = document.createElement('li');
          li.appendChild(document.createTextNode(line));
          listEl.appendChild(li);
        });
      }
      range.insertNode(listEl);
      const newRange = document.createRange();
      newRange.selectNodeContents(listEl);
      newRange.collapse(false);
      if (selection) {
        selection.removeAllRanges();
        selection.addRange(newRange);
      }
      selectionRef.current = newRange;
    }

    emitChange();
    updateActiveState();
  };

  const applyListCommand = (command, listTag) => {
    const editor = editorRef.current;
    if (!editor) return;
    saveSelection();
    editor.focus({ preventScroll: true });
    restoreSelection();
    const before = editor.innerHTML;
    document.execCommand(command, false, null);
    const after = editor.innerHTML;
    if (before === after) {
      applyList(listTag);
      return;
    }
    saveSelection();
    emitChange();
    updateActiveState();
  };

  const handleBlur = () => {
    setFocused(false);
    const editor = editorRef.current;
    if (!editor) return;
    const sanitized = sanitizeRichText(editor.innerHTML);
    editor.innerHTML = sanitized;
    lastValueRef.current = sanitized;
    onChange(sanitized);
  };

  return (
    <div className={`rich-input ${className}`}>
      <div className="rich-toolbar">
        {toolbarActions.map((action) => (
          <button
            key={action.command}
            type="button"
            className={`rich-btn ${activeFormats[action.command] ? 'active' : ''}`}
            onMouseDown={(event) => handleToolbarAction(action.command, event)}
            data-tooltip={action.label}
            aria-label={action.label}
          >
            <action.icon className="w-4 h-4" />
          </button>
        ))}
        <button
          type="button"
          className={`rich-btn ${activeFormats.link ? 'active' : ''}`}
          onMouseDown={(event) => {
            event.preventDefault();
            const editor = editorRef.current;
            if (!editor) return;
            const selection = window.getSelection();
            if (selection && selection.rangeCount > 0) {
              const range = selection.getRangeAt(0);
              if (editor.contains(range.commonAncestorContainer)) {
                selectionRef.current = range;
              }
            }
          }}
          onClick={handleLink}
          data-tooltip="Insert link"
          aria-label="Insert link"
        >
          <LinkIcon className="w-4 h-4" />
        </button>
      </div>
      <div
        ref={editorRef}
        className="rich-editor input-glass"
        contentEditable
        data-placeholder={placeholder}
        onInput={emitChange}
        onKeyUp={() => {
          saveSelection();
          updateActiveState();
        }}
        onMouseUp={() => {
          saveSelection();
          updateActiveState();
        }}
        onFocus={() => setFocused(true)}
        onBlur={handleBlur}
        suppressContentEditableWarning
        {...rest}
      />
    </div>
  );
}

export default RichTextInput;
